const MAX_SECRET_LENGTH = 4096;

export async function readSecret(
  prompt = 'Online User API key: ',
  input: NodeJS.ReadStream = process.stdin,
  output: NodeJS.WriteStream = process.stderr,
): Promise<string> {
  if (!input.isTTY || typeof input.setRawMode !== 'function') {
    const chunks: Buffer[] = [];
    let length = 0;
    for await (const chunk of input) {
      const bytes = Buffer.isBuffer(chunk) ? chunk : Buffer.from(String(chunk));
      length += bytes.length;
      if (length > MAX_SECRET_LENGTH) throw new Error('Secret input is too long');
      chunks.push(bytes);
    }
    return Buffer.concat(chunks).toString('utf8').trim();
  }

  output.write(prompt);
  const wasRaw = input.isRaw;
  const wasPaused = input.isPaused();
  input.setRawMode(true);
  input.resume();

  return await new Promise<string>((resolve, reject) => {
    let value = '';
    let settled = false;
    const finish = (error?: Error) => {
      if (settled) return;
      settled = true;
      input.off('data', onData);
      input.off('end', onEnd);
      input.off('close', onClose);
      input.off('error', onError);
      input.setRawMode(Boolean(wasRaw));
      if (wasPaused) input.pause();
      output.write('\n');
      if (error) reject(error);
      else resolve(value.trim());
    };
    const onData = (chunk: Buffer | string) => {
      const text = chunk.toString();
      for (const character of text) {
        if (character === '\u0003') {
          finish(new Error('Secret input cancelled'));
          return;
        }
        if (character === '\r' || character === '\n') {
          finish();
          return;
        }
        if (character === '\u007f' || character === '\b') {
          value = value.slice(0, -1);
          continue;
        }
        value += character;
        if (value.length > MAX_SECRET_LENGTH) {
          finish(new Error('Secret input is too long'));
          return;
        }
      }
    };
    const onEnd = () => finish();
    const onClose = () => finish();
    const onError = (error: Error) => finish(error);
    input.on('data', onData);
    input.once('end', onEnd);
    input.once('close', onClose);
    input.once('error', onError);
  });
}
